/**
 * Projection for `PlatformMapScene` — the demo's `helpMap()` (demo line 3769)
 * and `helpNav()` (demo line 3834).
 *
 * `helpMap` drew a 1180 × 760 SVG with six horizontal lanes: the systems it
 * reads, one store, sixteen agents, ten modules, five feedback loops, and the
 * control tower over all of it. Every box was a link. This file is that
 * diagram as data — six lanes, each with its tiles — so the scene can lay it
 * out as a responsive stack of lanes rather than as fixed geometry.
 *
 * `helpNav` drew the seven sections with their pages and role dots. That is
 * the second half of the page, and it comes straight from `seed/roles.ts`'s
 * `NAVMAP`, joined to the real sidebar's icons in `shared/oem-nav.ts`.
 *
 * Pure: no React, no fetching. Transformation layer (layer 3).
 *
 * @see src/versions/v1/modules/help/PlatformMapScene.tsx
 * @see docs/logic/help/platform-map.md
 */

import type { LucideIcon } from 'lucide-react';
import { FLEET } from '../../data/core';
import { OEM_STATIC_COUNTS } from '../../shared/oem-counts';
import { OEM_SECTIONS } from '../../shared/oem-nav';
import { routeForView } from '../../shared/oem-routes';
import { AGENTS } from '../../shared/seed/agents';
import { CONNECTORS } from '../../shared/seed/connectors';
import { LOOPS } from '../../shared/seed/loops';
import { AGENT_COLOURS, MODULES } from '../../shared/seed/modules';
import { NAVMAP, ROLE, type NavMapSection, type RoleId } from '../../shared/seed/roles';
import { WORKFLOW_STEPS } from '../../shared/seed/workflow-steps';

// ---------------------------------------------------------------------------
// The six lanes
// ---------------------------------------------------------------------------

/** One box inside a lane. */
export interface MapTile {
  readonly id: string;
  /** Leading marker — a module or loop number. */
  readonly index?: string;
  readonly label: string;
  /** Second line. */
  readonly detail?: string;
  /** Swatch, straight off the seed. Data, so it is an inline style. */
  readonly colour?: string;
  /** Route, or `null` for a tile with nowhere to go. */
  readonly to: string | null;
}

/** One horizontal band of the demo's map. */
export interface MapLane {
  readonly id: string;
  /** The uppercase lane label. */
  readonly title: string;
  /** The lane's own sub-line. */
  readonly subtitle: string;
  readonly tiles: readonly MapTile[];
  /** Tile density — the lane's natural column count on a wide viewport. */
  readonly columns: 2 | 4 | 5 | 8 | 10;
}

/**
 * Demo line 2842 — `sysFam`, the ten system families the map's bottom lane
 * draws. Ported here rather than into `shared/seed/`: it is presentation
 * grouping for one diagram, not a data-pack entity, and the seeds are frozen.
 *
 * The fourth tuple element was the demo's inline `show(...)` handler; it is
 * carried as a `[view, sub]` pair and resolved through `oem-routes.ts`.
 */
export const SYSTEM_FAMILIES: readonly {
  readonly name: string;
  readonly what: string;
  readonly systems: readonly string[];
  readonly view: readonly [string, string?];
}[] = [
  { name: 'Telematics / IoT', what: 'machine data', systems: ['Underground Wi-Fi / LTE telematics', 'RMS', 'Pitram', 'Rig control system'], view: ['connectors'] },
  { name: 'Technical data', what: 'manuals, bulletins, parts books', systems: ['CatBase', 'SharePoint', 'OEM bulletin service', 'Training video & AR headset'], view: ['data'] },
  { name: 'Customer portal', what: 'fleet, parts, contracts', systems: ['Salesforce', 'Customer satisfaction survey'], view: ['customers'] },
  { name: 'ERP', what: 'finance, procurement', systems: ['SAP PM'], view: ['costs'] },
  { name: 'CMMS', what: 'work orders, maintenance', systems: ['IBM Maximo · service register', 'IBM Maximo · Gawler Deep EAM', 'Maximo Mobile · technician app'], view: ['connectors'] },
  { name: 'WMS', what: 'inventory, warehouses', systems: ['Site stores & DC stock'], view: ['partsflow', 'inventory'] },
  { name: 'TMS', what: 'logistics, freight', systems: ['Freight & transit'], view: ['partsflow'] },
  { name: 'CRM', what: 'customers, contracts', systems: ['Salesforce', 'Outlook', 'Microsoft Teams'], view: ['customers'] },
  { name: 'Suppliers & partners', what: 'quality, availability', systems: ['Oil analysis lab portal', 'Tyre management'], view: ['connectors'] },
  { name: 'Other systems', what: 'HR, training, remote support', systems: ['LMS', 'Rostering / HR', 'XReach'], view: ['connectors'] },
];

/** Demo line 3781. Steps inside a module — the `+1` on `orchestrate` is the case trace. */
export const stepCount = (moduleId: string): number =>
  WORKFLOW_STEPS.filter((s) => s.mod === moduleId).length + (moduleId === 'orchestrate' ? 1 : 0);

/**
 * Demo lines 3779–3825. The six lanes, bottom to top as the demo reads them:
 * systems, store, agents, modules, loops, tower.
 */
export function mapLanes(): MapLane[] {
  return [
    {
      id: 'systems',
      title: 'The systems it reads',
      subtitle:
        'the OEM’s own, the customers’ and the third parties’ — each still producing what it already produces',
      columns: 5,
      tiles: SYSTEM_FAMILIES.map((family) => ({
        id: family.name,
        label: family.name,
        detail: `${family.what} · ${family.systems.length} system${family.systems.length === 1 ? '' : 's'}`,
        to: routeForView(family.view[0], family.view[1]),
      })),
    },
    {
      id: 'store',
      title: 'One store',
      subtitle: 'tables, documents, email, transcripts, machine logs and a record per machine',
      columns: 2,
      tiles: [
        {
          id: 'store',
          label: `${OEM_STATIC_COUNTS.data} raw source files`,
          detail: `${CONNECTORS.length} connectors · 14 native formats · a Maximo record for every one of ${FLEET.length} machines`,
          to: '/integration/data',
        },
      ],
    },
    {
      id: 'agents',
      title: `${AGENTS.length === 16 ? 'Sixteen' : AGENTS.length} agents`,
      subtitle:
        'each with a defined input, a defined output and a point where a person approves',
      columns: 8,
      tiles: AGENTS.map((agent) => ({
        id: agent.name,
        label: agent.name,
        colour: AGENT_COLOURS[agent.name] ?? 'var(--accent)',
        to: `/agents?agent=${encodeURIComponent(agent.name)}`,
      })),
    },
    {
      id: 'modules',
      title: 'The ten modules',
      subtitle:
        'every machine and every job moves through the same ten — click one to open it',
      columns: 10,
      tiles: MODULES.map((m) => ({
        id: m.id,
        index: String(m.n).padStart(2, '0'),
        label: m.short,
        detail: `${stepCount(m.id)} steps`,
        colour: m.col,
        to: `/workflows/modules/${m.id}`,
      })),
    },
    {
      id: 'loops',
      title: 'Five feedback loops',
      subtitle: 'the outcome of every job goes back into the front of the model',
      columns: 5,
      tiles: LOOPS.map((l) => ({
        id: l.id,
        index: String(l.n).padStart(2, '0'),
        label: l.name,
        detail: `${l.agent} agent`,
        colour: l.col,
        to: `/loops/${l.id}`,
      })),
    },
    {
      id: 'tower',
      title: 'Control tower',
      subtitle: 'eight measures the aftermarket P&L is run on',
      columns: 2,
      tiles: [
        {
          id: 'asset360',
          label: 'Asset360',
          detail:
            'Eight measures, each coloured on track, watch or needs action — and each one opens the rows behind it',
          to: '/',
        },
      ],
    },
  ];
}

/** Demo line 3826 — the arrow that closes the circle, as a caption. */
export const RETURN_CAPTION = 'outcomes feed the front of the model again';

// ---------------------------------------------------------------------------
// Reading the map
// ---------------------------------------------------------------------------

/** One "read it upward" / "and back down" / "what to look for" row. */
export interface ReadingRow {
  readonly heading: string;
  readonly body: string;
}

/** One of the three explainer cards under the map. */
export interface ReadingCard {
  readonly title: string;
  readonly rows: readonly ReadingRow[];
}

/** Demo line 3903–3909 — the three cards, verbatim. */
export const READING_CARDS: readonly ReadingCard[] = [
  {
    title: 'Read it upward',
    rows: [
      {
        heading: 'Systems → store',
        body: 'Each system keeps producing what it already produces; the store holds it in that format and cross-references it. Nothing is replaced.',
      },
      {
        heading: 'Store → agents',
        body: 'Sixteen specialists read the store. Each has a defined input, a defined output and a point where a person approves.',
      },
      {
        heading: 'Agents → modules',
        body: 'Every machine and every job moves through the same ten modules, and sits in exactly one of them at a time.',
      },
    ],
  },
  {
    title: 'And back down',
    rows: [
      {
        heading: 'Modules → loops',
        body: 'The outcome of every closed job goes into one of five loops rather than into a report nobody reads.',
      },
      {
        heading: 'Loops → the front',
        body: 'A loop changes the model, the plan, the stock position or the proposal the front end runs on tomorrow.',
      },
      {
        heading: 'All of it → the tower',
        body: 'Eight measures. Each is a number you can click down to the rows behind it.',
      },
    ],
  },
  {
    title: 'What to look for',
    rows: [
      {
        heading: 'Every figure is traceable',
        body: 'A dotted underline means the number opens the rows it was computed from.',
      },
      {
        heading: 'Nothing is autonomous',
        body: 'Level 2 throughout — agents draft, people approve. The approvals queue is the proof.',
      },
      {
        heading: 'One case, one owner',
        body: 'Whatever the channel, work becomes a case with a machine, a priority, an owner and a clock.',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// The section map
// ---------------------------------------------------------------------------

/** One page tile inside a section column. */
export interface SectionPage {
  readonly title: string;
  readonly question: string;
  readonly roles: readonly RoleId[];
  readonly to: string | null;
}

/** One column of the demo's `.navmap`, joined to the real sidebar's icon. */
export interface SectionColumn {
  readonly key: string;
  readonly name: string;
  readonly colour: string;
  readonly what: string;
  readonly who: readonly RoleId[];
  readonly icon: LucideIcon | null;
  readonly to: string | null;
  readonly pages: readonly SectionPage[];
}

/** Section icons come from the real nav, so the two cannot drift. */
const SECTION_ICON: Readonly<Record<string, LucideIcon>> = Object.fromEntries(
  OEM_SECTIONS.map((s) => [s.id, s.icon]),
);

/** Demo line 3834. `helpNav()` — the seven section columns, in demo order. */
export function sectionColumns(): SectionColumn[] {
  return NAVMAP.map((section: NavMapSection) => ({
    key: section.key,
    name: section.name,
    colour: section.colour,
    what: section.what,
    who: section.who,
    icon: SECTION_ICON[section.key] ?? null,
    to: routeForView('section', section.key),
    pages: section.items.map((item) => ({
      title: item.title,
      question: item.question,
      roles: item.roles,
      to: routeForView(item.action.view, item.action.sub),
    })),
  }));
}

/** Chip / dot colour for a role. The hexes are the demo's, carried as data. */
export const roleColour = (id: RoleId): string => ROLE[id].colour;

/** Chip label for a role. */
export const roleLabel = (id: RoleId): string => ROLE[id].label;

/** Demo line 3960 — the four figures in the Help hero. */
export function helpStats(): { eyebrow: string; value: string; detail: string }[] {
  return [
    { eyebrow: 'Sections', value: String(NAVMAP.length), detail: 'each with its own page' },
    {
      eyebrow: 'Modules',
      value: String(MODULES.length),
      detail: `and ${WORKFLOW_STEPS.length} steps inside them`,
    },
    {
      eyebrow: 'Agents',
      value: String(AGENTS.length),
      detail: `over ${CONNECTORS.length} connected systems`,
    },
    { eyebrow: 'Roles', value: '6', detail: 'in the VIEWING AS picker' },
  ];
}
